import type { NavNode, NavSection } from "./types"

/**
 * Pure queries over the nav tree.
 *
 * Kept out of `context.tsx` so that file exports components alone — mixing a
 * provider with plain functions costs the module its fast-refresh boundary, and
 * these are the parts most worth testing without a React tree around them.
 */

/**
 * True when the node itself is the active route.
 *
 * Matching is exact by default. Prefix matching cannot be the default because
 * the root node's href is a prefix of every other route — `/ui` would claim
 * `/ui/ads/google`. Nodes that genuinely own a subtree opt in through `match`,
 * which is how the platform's route constants already express it.
 */
export function matchesNode(node: NavNode, currentPath: string): boolean {
  if (node.match) return node.match(currentPath)
  return node.href !== undefined && node.href !== "#" && currentPath === node.href
}

/** Walks the tree and returns the ids of every ancestor of the active route. */
export function findAncestorIds(sections: NavSection[], currentPath: string): string[] {
  const trail: string[] = []

  const visit = (node: NavNode): boolean => {
    const childMatches = (node.children ?? []).some(section =>
      section.items.some(child => visit(child)),
    )
    if (childMatches) {
      trail.push(node.id)
      return true
    }
    return matchesNode(node, currentPath)
  }

  sections.forEach(section => section.items.forEach(item => visit(item)))
  return trail
}

/**
 * Returns the full node chain from root to the active route, root first.
 * The breadcrumb bar renders this instead of walking route constants again.
 */
export function findTrail(sections: NavSection[], currentPath: string): NavNode[] {
  const walk = (node: NavNode): NavNode[] | null => {
    for (const section of node.children ?? []) {
      for (const child of section.items) {
        const found = walk(child)
        if (found) return [node, ...found]
      }
    }
    return matchesNode(node, currentPath) ? [node] : null
  }

  for (const section of sections) {
    for (const item of section.items) {
      const found = walk(item)
      if (found) return found
    }
  }
  return []
}
